import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { getEligibleClaimEvents } from "../../services/disputeService";
import Loader from "../common/Loader";
import Button from "../common/Button";
import Modal from "../common/Modal";
import Badge from "../common/Badge";
import EmptyState from "../common/EmptyState";
import { color, radius } from "../../styles/theme";

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" }) : "");

/**
 * Inline event/gallery picker for the New Dispute form — replaces mobile's
 * separate PaidEvents screen (src/Screens/paid-events, purchase-card), which
 * exists only to feed this one field.
 */
const EligibleEventPicker = ({ onClose, onSelect }) => {
  const { t } = useTranslation(["disputes", "common"]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  useEffect(() => {
    getEligibleClaimEvents({ pageNumber: 1, pageSize: 100 })
      .then((res) => setItems(res.data?.Result || []))
      .catch(() => toast.error(t("eligibleEventPicker.loadError")))
      .finally(() => setLoading(false));
  }, [t]);

  const handleDone = () => {
    if (selectedIndex >= 0) onSelect(items[selectedIndex]);
  };

  return (
    <Modal
      title={t("eligibleEventPicker.title")}
      onClose={onClose}
      maxWidth={480}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t("common:actions.cancel")}</Button>
          <Button onClick={handleDone} disabled={selectedIndex < 0}>{t("eligibleEventPicker.select")}</Button>
        </>
      }
    >
      {loading ? (
        <Loader />
      ) : items.length === 0 ? (
        <EmptyState text={t("eligibleEventPicker.emptyState")} />
      ) : (
        <div style={s.list}>
          {items.map((item, i) => {
            const isAlbum = item?.AlbumId != null;
            const itemLabel = isAlbum ? t("eligibleEventPicker.album") : t("eligibleEventPicker.gallery");
            const itemName = isAlbum ? item?.AlbumName : item?.GalleryName;
            const isSelected = selectedIndex === i;
            return (
              <button
                type="button"
                key={`${item?.EventGalleryId}-${item?.AlbumId ?? "g"}-${i}`}
                style={{ ...s.card, ...(isSelected ? s.cardSelected : {}) }}
                onClick={() => setSelectedIndex(i)}
              >
                <div style={s.cardRow}>
                  <div style={s.eventRow}>
                    {item?.EventThumbnail && <img src={item.EventThumbnail} alt="" style={s.thumb} />}
                    <span style={s.eventName}>{item?.EventName}</span>
                  </div>
                  <Badge variant="success">{t("eligibleEventPicker.paid")}</Badge>
                </div>
                <div style={s.cardRow}>
                  <span style={s.typeText}><b>{itemLabel}</b>: {itemName}</span>
                </div>
                <div style={s.cardRow}>
                  <span style={s.amount}>{t("eligibleEventPicker.amount", { amount: item?.AmountPaid, currency: item?.Currency || "EUR" })}</span>
                  {item?.PaidAt && <span style={s.date}>{formatDate(item.PaidAt)}</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </Modal>
  );
};

const s = {
  list: { display: "flex", flexDirection: "column", gap: 10 },
  card: {
    textAlign: "left", background: color.surface, border: `1.5px solid ${color.border}`, borderRadius: radius.md,
    padding: "12px 14px", cursor: "pointer", width: "100%",
  },
  cardSelected: { border: `1.5px solid ${color.primary500}`, background: color.primary50 },
  cardRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 },
  eventRow: { display: "flex", alignItems: "center", gap: 10 },
  thumb: { width: 28, height: 28, borderRadius: 14, objectFit: "cover" },
  eventName: { fontSize: 14, fontWeight: 700, color: color.textPrimary },
  typeText: { fontSize: 12.5, color: color.textSecondary },
  amount: { fontSize: 13, fontWeight: 600, color: color.gray800 },
  date: { fontSize: 11, color: color.textMuted },
};

export default EligibleEventPicker;
